import React, { useEffect, useState } from 'react';
import { Box, Divider, List, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faEnvelopeOpen } from '@fortawesome/sharp-regular-svg-icons';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
    type MessageItem,
    type MessageItemDetail,
    useWebsocketActions,
    useWebsocketState,
} from '@europrocurement/l2d-websocket';
import { Link } from 'react-router-dom';
import { useUser } from '@europrocurement/l2d-modules/modules/Utilisateur';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { FlexySelect, useL2DTheme, type OptionType } from '@europrocurement/flexy-components';
import { APIS_SERVICE } from '@europrocurement/l2d-modules/hooks/useMessagerieNotification';

const MESSAGE_SIZE_OPTIONS = [5, 10, 15, 20];

const getMessageContent = (message: MessageItemDetail): React.ReactNode => {
    switch (message.type) {
        case 'message':
            return message.message;
        case 'document':
            return `Document : ${message.dosDocFileName}`;
        case 'emoji':
            return <FaOptionIcon icon={message.icon as IconProp} />;
        default:
            return '';
    }
};

const formatDate = (date: string): string => {
    const normalized = date.replace('+00:00Z', 'Z');
    return new Date(normalized).toLocaleString();
};

const NotificationWidgetDashboard: React.FunctionComponent = function () {
    const { socketOpen, lastMessages } = useWebsocketState();
    const { getLastMessages } = useWebsocketActions();
    const { fetchUser, cache } = useUser();
    const [limit, setLimit] = useState(MESSAGE_SIZE_OPTIONS[0]);
    const { palette } = useL2DTheme();

    useEffect(() => {
        if (socketOpen) {
            getLastMessages(undefined, limit);
        }
    }, [socketOpen, getLastMessages, limit]);

    useEffect(() => {
        lastMessages.forEach((item: MessageItem) => {
            if (item.data.userIdentifier && !(item.data.userIdentifier in cache)) {
                fetchUser(item.data.userIdentifier);
            }
        });
    }, [lastMessages, fetchUser, cache]);

    const handleLimitChange = (option: unknown) => {
        if (option) setLimit((option as OptionType).value as number);
    };

    return (
        <Box
            sx={{
                background: palette.background.paper,
                borderRadius: '24px',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 2,
                    px: 1,
                }}
            >
                <Typography variant="l2dh6">
                    Derniers messages
                    {lastMessages.length > 0 &&
                    lastMessages.some(
                        (item) =>
                            item.data.reader.interne === 'false' ||
                            item.data.reader.interne === false,
                    ) ? (
                        <Typography
                            component="span"
                            variant="body2"
                            color={
                                lastMessages.some(
                                    (item) =>
                                        item.data.reader.interne === 'false' ||
                                        item.data.reader.interne === false,
                                )
                                    ? palette.error.main
                                    : palette.text.secondary
                            }
                            sx={{ ml: 1 }}
                        >
                            {`(${lastMessages.filter((item) => item.data.reader.interne === 'false' || item.data.reader.interne === false).length} / ${lastMessages.length} non lu(s))`}
                        </Typography>
                    ) : null}
                </Typography>
                <Box sx={{ width: 80 }}>
                    <FlexySelect
                        options={MESSAGE_SIZE_OPTIONS.map((s) => ({ value: s, label: String(s) }))}
                        value={{ value: limit, label: String(limit) }}
                        onChange={handleLimitChange}
                        size="small"
                        menuPortalTarget={document.body}
                    />
                </Box>
            </Box>
            {lastMessages.length === 0 ? (
                <Typography
                    variant="body2"
                    color={palette.text.secondary}
                >
                    Aucun message récent
                </Typography>
            ) : (
                <List disablePadding>
                    {lastMessages.map((item: MessageItem, index: number) => {
                        const isReadInterne =
                            item.data.reader.interne === 'true' ||
                            item.data.reader.interne === true;

                        const redirect = item.data.discriminant?.idDossierFormalite
                            ? `/dossiers/${item.data.discriminant.idDossierFormalite}?dossier-detail=message`
                            : '';
                        const { data } = item;
                        const user = cache[data.userIdentifier];
                        let title =
                            user?.firstName && user.lastName
                                ? `${user.firstName} ${user.lastName}`
                                : '';
                        if (data.userIdentifier === APIS_SERVICE.COMMUNICATION) {
                            title = 'Système L2D';
                        } else if (
                            data.discriminant?.isMigration &&
                            data.userIdentifier.split(' ').length > 1
                        ) {
                            title = `${data.userIdentifier.split(' ')[0]} ${data.userIdentifier.split(' ')[1]}`;
                        }
                        return (
                            <React.Fragment key={item.messageId}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexDirection: 'row',
                                    }}
                                >
                                    <Link
                                        to={redirect}
                                        target="_blank"
                                        style={{
                                            textDecoration: 'none',
                                            padding: '15px',
                                            width: '100%',
                                            color: palette.text.secondary,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexDirection: 'row',
                                                gap: '8px',
                                            }}
                                        >
                                            {' '}
                                            <FontAwesomeIcon
                                                color={
                                                    isReadInterne
                                                        ? palette.text.secondary
                                                        : palette.error.main
                                                }
                                                icon={isReadInterne ? faEnvelopeOpen : faEnvelope}
                                            />
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    width: '100%',
                                                }}
                                            >
                                                <Box>
                                                    <Typography
                                                        variant="l2dbodys"
                                                        color={palette.text.primary}
                                                    >
                                                        {title}
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Typography
                                                        color={palette.text.secondary}
                                                        variant="body2"
                                                    >
                                                        {`Le ${formatDate(item.data.date)} : `}
                                                        {getMessageContent(item.data.message)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Link>
                                </Box>
                                {index < lastMessages.length - 1 && (
                                    <Divider
                                        variant="inset"
                                        component="li"
                                    />
                                )}
                            </React.Fragment>
                        );
                    })}
                </List>
            )}
        </Box>
    );
};

export default NotificationWidgetDashboard;
